import {
  createContext,
  ReactNode,
  useContext,
  useState,
} from 'react'

import { ShipmentNotification } from '@/interfaces/shipmentNotification'
import { useShipmentNotifications } from '../hooks/useShipmentNotifications'

// Define a more complete interface for shipment notification data
interface ShipmentNotificationData {
  items: ShipmentNotification[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the context type
interface TableContextType {
  currentPage: number
  setCurrentPage: (page: number) => void
  isFetching: boolean
  isError: boolean
  pageSize: number
  setPageSize: (size: number) => void
  shipmentNotificationData: ShipmentNotificationData | null
  isLoading: boolean
  refetch: () => void
  // Add direct access to pagination methods for convenience
  goToNextPage: () => void
  goToPreviousPage: () => void
  // Skeleton data for loading state
  skeletonRowCount: number
  // Additional helpful properties
  hasNextPage: boolean
  hasPreviousPage: boolean
  // Filter management
  resetToFirstPage: () => void
}

const TableContext = createContext<TableContextType | undefined>(undefined)

export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Use our working hook for data, passing the pageSize
  const {
    data: shipmentNotifications,
    isLoading,
    error: isError,
    refetch,
  } = useShipmentNotifications(pageSize)

  // Simple pagination logic
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedData = shipmentNotifications.slice(startIndex, endIndex)
  const totalPages = Math.ceil(shipmentNotifications.length / pageSize)

  const shipmentNotificationData: ShipmentNotificationData = {
    items: paginatedData,
    totalItems: shipmentNotifications.length,
    currentPage,
    totalPages,
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const resetToFirstPage = () => {
    setCurrentPage(1)
  }

  const contextValue: TableContextType = {
    currentPage,
    setCurrentPage,
    isFetching: isLoading,
    isError: !!isError,
    pageSize,
    setPageSize,
    shipmentNotificationData,
    isLoading,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: 10,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    resetToFirstPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}
